import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero, SegmentedControl } from "../../../components/game";
import { useSportNames } from "../../../components/SportChips";

// Simple line chart component
function MiniLineChart({ data, color = "#8b5cf6" }: { data: number[]; color?: string }) {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const height = 40;
  const width = 100;
  const padding = 4;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * (width - 2 * padding);
    const y = height - padding - ((value - min) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      {/* Last point dot */}
      {data.length > 0 && (
        <circle
          cx={padding + ((data.length - 1) / (data.length - 1)) * (width - 2 * padding)}
          cy={height - padding - ((data[data.length - 1] - min) / range) * (height - 2 * padding)}
          r="3"
          fill={color}
        />
      )}
    </svg>
  );
}

interface MetricDefinition {
  id: string;
  name: string;
  unit: string | null;
  description: string | null;
  measurement_interval_days: number;
  latestValue: {
    id: string;
    value: number;
    measured_at: string;
    notes: string | null;
  } | null;
  history: {
    id: string;
    value: number;
    measured_at: string;
    notes: string | null;
  }[];
}

interface AthleteData {
  id: string;
  full_name: string;
  email: string;
  date_of_birth: string | null;
  gender: string | null;
  athlete_profiles: {
    team_id: string | null;
    club_id: string | null;
    team: {
      id: string;
      name: string;
      sport_type: string | null;
      age_group: string | null;
      age_group_ref: { name: string; min_age: number | null; max_age: number | null } | null;
    } | null;
    club: { id: string; name: string; city: string | null; sports: string[] | null } | null;
  } | null;
}

interface AthleteStats {
  points_total: number;
  attendance_rate: number;
  sessions_total: number;
  sessions_present: number;
  club_rank: number | null;
}

interface AthleteMetricsResponse {
  athlete: AthleteData;
  stats?: AthleteStats;
  definitions: MetricDefinition[];
}

export default function AppCoachAthleteMetrics() {
  const { t } = useTranslation();
  const sportName = useSportNames();
  const { athleteId } = useParams<{ athleteId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<AthleteMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state for adding new values
  const [values, setValues] = useState<{ [metricId: string]: string }>({});
  const [saving, setSaving] = useState(false);

  // View mode: "form" or "history"
  const [viewMode, setViewMode] = useState<"form" | "history">("form");

  useEffect(() => {
    if (!athleteId) return;
    loadData();
  }, [athleteId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/metrics/custom/athlete/${athleteId}`);
      setData(response.data);
      // Pre-fill with latest values if they exist
      const initialValues: { [key: string]: string } = {};
      for (const def of response.data.definitions) {
        if (def.latestValue) {
          initialValues[def.id] = def.latestValue.value.toString();
        }
      }
      setValues(initialValues);
    } catch (err: any) {
      console.error("Failed to load athlete metrics:", err);
      setError(err?.response?.data?.error || "Nu s-au putut incarca datele");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!athleteId) return;

    const valuesToSave = Object.entries(values)
      .filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      .map(([metricId, value]) => ({
        metric_id: metricId,
        value: parseFloat(value),
      }));

    if (valuesToSave.length === 0) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/metrics/custom/athlete/${athleteId}/bulk`, {
        values: valuesToSave,
        measured_at: new Date().toISOString(),
      });
      await loadData();
      alert(t("coach.metricsSaved", "Metricile au fost salvate!"));
    } catch (err: any) {
      console.error("Failed to save metrics:", err);
      alert(err?.response?.data?.error || "Eroare la salvare");
    }
    setSaving(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const calculateAge = (dob: string | null) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Calculate the next measurement date based on each metric's interval
  const nextMeasurementInfo = useMemo(() => {
    if (!data?.definitions || data.definitions.length === 0) return null;

    const today = new Date();
    let earliestNextDate: Date | null = null;
    let overdueDef: MetricDefinition | null = null;
    let soonestDef: MetricDefinition | null = null;

    // For each metric, calculate when it's next due
    for (const def of data.definitions) {
      if (def.latestValue?.measured_at) {
        const lastMeasured = new Date(def.latestValue.measured_at);
        const intervalDays = def.measurement_interval_days || 90;
        const nextDate = new Date(lastMeasured);
        nextDate.setDate(nextDate.getDate() + intervalDays);

        // Track the earliest due date
        if (!earliestNextDate || nextDate < earliestNextDate) {
          earliestNextDate = nextDate;
          soonestDef = def;
          if (nextDate < today) {
            overdueDef = def;
          }
        }
      } else {
        // No measurement ever - this is immediately due
        earliestNextDate = today;
        overdueDef = def;
        soonestDef = def;
        break; // Can't be earlier than today
      }
    }

    if (!earliestNextDate) return { nextDate: null, isOverdue: false, daysUntil: 0 };

    const diffTime = earliestNextDate.getTime() - today.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const isOverdue = daysUntil < 0;

    return {
      nextDate: earliestNextDate,
      isOverdue,
      daysUntil,
      metricName: soonestDef?.name || null
    };
  }, [data]);

  if (loading) {
    return (
      <MobileAppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </MobileAppLayout>
    );
  }

  if (error || !data) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center shadow-[0_3px_0_#fecaca]">
            <p className="text-red-600">{error || "Nu s-au gasit date"}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-xl font-bold active:translate-y-[2px] transition-transform"
            >
              {t("common.back", "Înapoi")}
            </button>
          </div>
        </div>
      </MobileAppLayout>
    );
  }

  const hasNoDefinitions = data.definitions.length === 0;

  return (
    <MobileAppLayout>
      <div className="pb-24">
        {/* Unified hero — back, athlete identity and the metrics-config gear
            in one dark header (was a plain title row + a separate card). */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="-ml-1.5 p-1.5 rounded-full hover:bg-white/10 transition-colors"
              aria-label={t("common.back", "Înapoi")}
            >
              <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <Link
              to="/app/coach/metrics/config"
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              title={t("coach.configureMetrics", "Configurare metrici")}
            >
              <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="w-16 h-16 rounded-2xl bg-white/15 grid place-items-center text-2xl font-black shrink-0">
              {data.athlete.full_name?.charAt(0) || "?"}
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-extrabold truncate">{data.athlete.full_name}</h1>
              <p className="text-white/70 text-[13px] mt-0.5 truncate">
                {[data.athlete.athlete_profiles?.team?.name || t("coach.noTeam", "Fără echipă"),
                  data.athlete.date_of_birth ? `${calculateAge(data.athlete.date_of_birth)} ${t("coach.yearsShort", "ani")}` : null]
                  .filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
        {/* Profile details — club, sport, age group, gender. Small
            card grid so the coach sees the org context at a glance
            without leaving the metrics page. */}
        {data.athlete.athlete_profiles && (
          <GameCard className="p-4">
            <h3 className="text-sm font-extrabold text-slate-700 mb-3">Detalii</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {data.athlete.athlete_profiles.club && (
                <div>
                  <p className="text-xs text-slate-500">Club</p>
                  <p className="font-medium text-slate-800 truncate">
                    {data.athlete.athlete_profiles.club.name}
                    {data.athlete.athlete_profiles.club.city && (
                      <span className="text-slate-500 font-normal"> · {data.athlete.athlete_profiles.club.city}</span>
                    )}
                  </p>
                </div>
              )}
              {data.athlete.athlete_profiles.team && (
                <div>
                  <p className="text-xs text-slate-500">Echipă</p>
                  <p className="font-medium text-slate-800 truncate">{data.athlete.athlete_profiles.team.name}</p>
                </div>
              )}
              {data.athlete.athlete_profiles.team?.sport_type && (
                <div>
                  <p className="text-xs text-slate-500">Sport</p>
                  <p className="font-medium text-slate-800 truncate">{sportName(data.athlete.athlete_profiles.team.sport_type)}</p>
                </div>
              )}
              {(data.athlete.athlete_profiles.team?.age_group_ref?.name || data.athlete.athlete_profiles.team?.age_group) && (
                <div>
                  <p className="text-xs text-slate-500">Grupă de vârstă</p>
                  <p className="font-medium text-slate-800 truncate">
                    {data.athlete.athlete_profiles.team.age_group_ref?.name || data.athlete.athlete_profiles.team.age_group}
                  </p>
                </div>
              )}
              {data.athlete.gender && (
                <div>
                  <p className="text-xs text-slate-500">Gen</p>
                  <p className="font-medium text-slate-800 capitalize">
                    {data.athlete.gender === "male" ? "Masculin" : data.athlete.gender === "female" ? "Feminin" : data.athlete.gender}
                  </p>
                </div>
              )}
            </div>
          </GameCard>
        )}

        {/* Stats grid — points, attendance, sessions, rank. Real
            aggregates from the backend, not the modal's old zeros. */}
        {data.stats && (
          <div className="grid grid-cols-2 gap-3">
            <GameCard className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472a4.265 4.265 0 01.264-.521z" />
                  </svg>
                </div>
                <p className="text-xs text-slate-500">Puncte</p>
              </div>
              <p className="text-2xl font-extrabold text-slate-800 tabular-nums">{data.stats.points_total}</p>
            </GameCard>
            <GameCard className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-xs text-slate-500">Prezență</p>
              </div>
              <p className="text-2xl font-extrabold text-slate-800 tabular-nums">{data.stats.attendance_rate}%</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {data.stats.sessions_present} din {data.stats.sessions_total} ședințe
              </p>
            </GameCard>
            <GameCard className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-xs text-slate-500">Ședințe</p>
              </div>
              <p className="text-2xl font-extrabold text-slate-800 tabular-nums">{data.stats.sessions_total}</p>
            </GameCard>
            <GameCard className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-xs text-slate-500">Rang în club</p>
              </div>
              <p className="text-2xl font-extrabold text-slate-800 tabular-nums">
                {data.stats.club_rank ? `#${data.stats.club_rank}` : "—"}
              </p>
            </GameCard>
          </div>
        )}

        {/* Next Measurement & Evolution Chart */}
        {!hasNoDefinitions && data.definitions.some(d => d.history.length > 0) && (
          <>
            {/* Next Measurement Date */}
            {nextMeasurementInfo && nextMeasurementInfo.nextDate && (
              <div className={`rounded-xl p-4 ${nextMeasurementInfo.isOverdue ? "bg-red-50 border border-red-200" : nextMeasurementInfo.daysUntil <= 14 ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${nextMeasurementInfo.isOverdue ? "bg-red-100" : nextMeasurementInfo.daysUntil <= 14 ? "bg-amber-100" : "bg-emerald-100"}`}>
                    <svg className={`w-5 h-5 ${nextMeasurementInfo.isOverdue ? "text-red-600" : nextMeasurementInfo.daysUntil <= 14 ? "text-amber-600" : "text-emerald-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${nextMeasurementInfo.isOverdue ? "text-red-800" : nextMeasurementInfo.daysUntil <= 14 ? "text-amber-800" : "text-emerald-800"}`}>
                      {nextMeasurementInfo.isOverdue
                        ? t("coach.measurementOverdue", "Măsurarea este întârziată!")
                        : nextMeasurementInfo.daysUntil <= 14
                        ? t("coach.measurementSoon", "Măsurare in curând")
                        : t("coach.nextMeasurement", "Următoarea măsurare")}
                      {nextMeasurementInfo.metricName && (
                        <span className="font-normal"> - {nextMeasurementInfo.metricName}</span>
                      )}
                    </p>
                    <p className={`text-xs ${nextMeasurementInfo.isOverdue ? "text-red-600" : nextMeasurementInfo.daysUntil <= 14 ? "text-amber-600" : "text-emerald-600"}`}>
                      {formatDate(nextMeasurementInfo.nextDate.toISOString())}
                      {nextMeasurementInfo.isOverdue
                        ? ` (${Math.abs(nextMeasurementInfo.daysUntil)} ${t("coach.daysAgo", "zile întârziere")})`
                        : ` (${nextMeasurementInfo.daysUntil} ${t("coach.daysLeft", "zile ramase")})`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Metrics Evolution Chart */}
            <GameCard className="overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="font-extrabold text-slate-800">{t("coach.metricsEvolution", "Evoluție Metrici")}</h3>
              </div>
              <div className="p-4 space-y-3">
                {data.definitions.filter(d => d.history.length > 1).map(def => {
                  // Reverse history to show oldest to newest for chart
                  const chartData = [...def.history].reverse().map(h => h.value);
                  const latestValue = def.history[0]?.value;
                  const previousValue = def.history[1]?.value;
                  const change = latestValue && previousValue ? latestValue - previousValue : 0;
                  const changePercent = previousValue ? ((change / previousValue) * 100).toFixed(1) : "0";

                  return (
                    <div key={def.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-700 text-sm truncate">{def.name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-extrabold text-indigo-600">
                            {latestValue} {def.unit || ""}
                          </span>
                          {change !== 0 && (
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${change > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                              {change > 0 ? "+" : ""}{change.toFixed(1)} ({change > 0 ? "+" : ""}{changePercent}%)
                            </span>
                          )}
                        </div>
                      </div>
                      <MiniLineChart data={chartData} color={change >= 0 ? "#10b981" : "#ef4444"} />
                    </div>
                  );
                })}
                {data.definitions.filter(d => d.history.length > 1).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {t("coach.needMoreData", "Sunt necesare cel putin 2 măsurători pentru a vedea evoluția")}
                  </p>
                )}
              </div>
            </GameCard>
          </>
        )}

        {/* No Definitions Warning */}
        {hasNoDefinitions ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center shadow-[0_3px_0_#fde68a]">
            <svg className="w-12 h-12 text-amber-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="font-extrabold text-amber-800 mb-2">{t("coach.noMetricsConfigured", "Nu ai configurat metrici")}</h3>
            <p className="text-sm text-amber-700 mb-4">
              {t("coach.noMetricsConfiguredDesc", "Pentru a înregistra metrici trebuie sa configurezi ce metrici vrei sa urmaresti.")}
            </p>
            <Link
              to="/app/coach/metrics/config"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-bold shadow-[0_3px_0_#b45309] active:translate-y-[2px] transition-transform"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t("coach.configureMetrics", "Configurare metrici")}
            </Link>
          </div>
        ) : (
          <>
            {/* View Toggle */}
            <SegmentedControl<"form" | "history">
              className="w-full [&>button]:flex-1"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: "form", label: t("coach.enterValues", "Introducere valori") },
                { value: "history", label: t("coach.history", "Istoric") },
              ]}
            />

            {viewMode === "form" ? (
              /* Entry Form */
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  {t("coach.enterCurrentValues", "Introdu valorile curente pentru fiecare metrica:")}
                </p>

                {data.definitions.map((def) => (
                  <GameCard key={def.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-extrabold text-slate-800">
                        {def.name}
                        {def.unit && <span className="text-slate-400 font-medium ml-1">({def.unit})</span>}
                      </label>
                      {def.latestValue && (
                        <span className="text-xs text-slate-400">
                          {t("coach.lastValue", "Ultima")}: {def.latestValue.value} {def.unit || ""}
                        </span>
                      )}
                    </div>
                    {def.description && (
                      <p className="text-xs text-slate-500 mb-2">{def.description}</p>
                    )}
                    <input
                      type="number"
                      step="any"
                      value={values[def.id] || ""}
                      onChange={(e) => setValues({ ...values, [def.id]: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={def.latestValue ? def.latestValue.value.toString() : "0"}
                    />
                    {def.latestValue && (
                      <p className="text-xs text-slate-400 mt-2">
                        {t("coach.lastMeasured", "Ultima măsurare")}: {formatDate(def.latestValue.measured_at)}
                      </p>
                    )}
                  </GameCard>
                ))}

                {/* Save Button */}
                <GameButton
                  variant="mint"
                  onClick={handleSave}
                  disabled={saving || Object.values(values).every(v => v === "" || v === undefined)}
                >
                  {saving ? t("common.saving", "Se salvează...") : t("coach.saveMetrics", "Salvează Metricile")}
                </GameButton>
              </div>
            ) : (
              /* History View */
              <div className="space-y-4">
                {data.definitions.map((def) => (
                  <GameCard key={def.id} className="overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                      <h3 className="font-extrabold text-slate-800">
                        {def.name}
                        {def.unit && <span className="text-slate-400 font-medium ml-1">({def.unit})</span>}
                      </h3>
                    </div>
                    {def.history.length === 0 ? (
                      <div className="p-4 text-center text-slate-400 text-sm">
                        {t("coach.noHistoryYet", "Nu există înregistrări")}
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {def.history.map((h, i) => (
                          <div key={h.id} className={`px-4 py-3 flex items-center justify-between ${i === 0 ? "bg-indigo-50" : ""}`}>
                            <div>
                              <p className="text-sm text-slate-600">{formatDate(h.measured_at)}</p>
                              {h.notes && <p className="text-xs text-slate-400">{h.notes}</p>}
                            </div>
                            <div className="text-right">
                              <p className={`font-extrabold ${i === 0 ? "text-indigo-600" : "text-slate-700"}`}>
                                {h.value} {def.unit || ""}
                              </p>
                              {i > 0 && def.history[i - 1] && (
                                <p className={`text-xs ${h.value > def.history[i - 1].value ? "text-emerald-600" : h.value < def.history[i - 1].value ? "text-red-600" : "text-slate-400"}`}>
                                  {h.value > def.history[i - 1].value ? "+" : ""}{(h.value - def.history[i - 1].value).toFixed(1)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </GameCard>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
